import { getConsent } from '@/lib/consent';
import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import { NEXT_PUBLIC_PODSCRIBE_DISABLED } from '@/lib/env';

const UK_SITE_ID = '8';
const PODSCRIBE_KEY_PREFIXES = ['__ps_', '__podscribe_', '__pscrb_', 'TapAd_'];
const MAX_PENDING_EVENTS = 20;

declare global {
  interface Window {
    podscribe?: (command: string, data?: Record<string, unknown>) => void;
  }
}

type PendingEvent = [command: string, data?: Record<string, unknown>];

let enabled = false;
let pendingEvents: PendingEvent[] = [];

export const isPodscribeConfigured = (): boolean =>
  !NEXT_PUBLIC_PODSCRIBE_DISABLED && process.env.NEXT_PUBLIC_SITE_ID === UK_SITE_ID;

export const isPodscribeEnabled = (): boolean => enabled && isPodscribeConfigured();

const isPodscribeKey = (key: string): boolean =>
  PODSCRIBE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

const clearPodscribeStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  clearBrowserCookies(isPodscribeKey);
  clearWebStorage(() => window.localStorage, isPodscribeKey);
  clearWebStorage(() => window.sessionStorage, isPodscribeKey);
};

export const enablePodscribe = (): boolean => {
  enabled = typeof window !== 'undefined' && isPodscribeConfigured() && getConsent().allowed;
  return enabled;
};

export const sendPodscribeEvent = (command: string, data?: Record<string, unknown>): void => {
  if (!isPodscribeEnabled()) {
    return;
  }

  if (typeof window.podscribe === 'function') {
    window.podscribe(command, data);
    return;
  }

  pendingEvents = [...pendingEvents.slice(-(MAX_PENDING_EVENTS - 1)), [command, data]];
};

export const flushPodscribeEvents = (): void => {
  if (!isPodscribeEnabled() || typeof window.podscribe !== 'function') {
    return;
  }

  const events = pendingEvents;
  pendingEvents = [];
  events.forEach(([command, data]) => window.podscribe?.(command, data));
};

export const disablePodscribe = (): void => {
  enabled = false;
  pendingEvents = [];
  clearPodscribeStorage();
};
